import { useEffect } from "react";

// ---------------------------------------------------------------------------
// usePageMeta — sets document.title + meta description + OG tags per page.
//
// On unmount, reverts to the default title so navigating away from a
// page doesn't leave stale meta. The OG tags are left as-is because
// crawlers only read the initial HTML (SSR / prerender territory).
// For an SPA the index.html defaults handle crawlers; these dynamic
// updates improve the in-browser tab title and share-via-browser flows.
// ---------------------------------------------------------------------------

const DEFAULT_TITLE = "FairPath | Property Tax Evidence Packets for Homeowners";
const BASE_URL = "https://fairpath.app";
const DEFAULT_OG_IMAGE = `${BASE_URL}/images/og-default.png`;

interface PageMeta {
  /** Browser tab title. Also used for og:title and twitter:title unless overridden. */
  title: string;
  /** Meta description. Also used for og:description and twitter:description unless overridden. */
  description: string;
  /** Canonical path, e.g. "/pricing". Appended to BASE_URL for og:url. */
  path?: string;
  /** Override OG image URL. Defaults to /images/og-default.jpg. */
  ogImage?: string;
  /** Override og:title (and twitter:title) independently of the browser tab title. */
  ogTitle?: string;
  /** Override og:description (and twitter:description) independently of the meta description. */
  ogDescription?: string;
}

function setMetaTag(property: string, content: string, isOg = false) {
  const attr = isOg ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.content = content;
}

/** Build the full canonical URL. "/" → bare domain (no trailing slash). */
function canonicalUrl(path: string): string {
  return path === "/" ? BASE_URL : `${BASE_URL}${path}`;
}

function setCanonicalLink(href: string) {
  let link = document.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}

export function usePageMeta({ title, description, path, ogImage, ogTitle, ogDescription }: PageMeta) {
  useEffect(() => {
    // Tab title
    document.title = title;

    // Meta description
    setMetaTag("description", description);

    // Open Graph
    setMetaTag("og:title", ogTitle ?? title, true);
    setMetaTag("og:description", ogDescription ?? description, true);
    setMetaTag("og:image", ogImage ?? DEFAULT_OG_IMAGE, true);
    if (path) {
      const url = canonicalUrl(path);
      setMetaTag("og:url", url, true);
      setCanonicalLink(url);
    }

    // Twitter Card
    setMetaTag("twitter:title", ogTitle ?? title);
    setMetaTag("twitter:description", ogDescription ?? description);

    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [title, description, path, ogImage, ogTitle, ogDescription]);
}

/** Retained issued links contain private identifiers, not indexable page URLs. */
export function useNoIndexMeta(): void {
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const meta = existing ?? document.createElement("meta");
    const previousContent = existing?.content ?? null;
    if (!existing) {
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "noindex, nofollow";
    return () => {
      if (existing && previousContent !== null) existing.content = previousContent;
      else meta.remove();
    };
  }, []);
}
